import { useFeatureValue } from '@growthbook/growthbook-react';
import { useMemo } from 'react';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { PaymentTransactionType, PAYMENT_GATEWAY, PAYMENT_TRANSACTION_TYPE } from '../../types/enums';
import { PaymentProvider } from '../../types/payment';
import {
  resolveFinpspDepositTypes,
  resolveFinpspWithdrawalTypes,
  type FinpspGatewayOptions,
} from '../../helpers/payment/finpspConfig';

const usePaymentProviders = (
  paymentProviders: PaymentProvider[],
  transactionType: PaymentTransactionType,
  paymentGateway: string
) => {
  const paymentGatewayOptions = useFeatureValue<FinpspGatewayOptions[]>(FEATURE_FLAG.PAYMENT_GATEWAY_OPTIONS, []);

  const filteredProviders = useMemo(() => {
    const gatewayConfig = paymentGatewayOptions.find((gateway) => gateway.id === paymentGateway);
    const enabledProviders = gatewayConfig?.providers?.map((provider) => provider.id);
    const hasEnabledProvidersFilter = !!enabledProviders?.length;

    return paymentProviders.filter((provider) => {
      const providerIds = [provider.value, ...(provider.aliases ?? [])];
      const isEnabled = !hasEnabledProvidersFilter || providerIds.some((id) => enabledProviders.includes(id));
      if (!isEnabled) {
        return false;
      }

      if (paymentGateway === PAYMENT_GATEWAY.FINPSP) {
        const types =
          transactionType === PAYMENT_TRANSACTION_TYPE.WITHDRAW
            ? resolveFinpspWithdrawalTypes(gatewayConfig, provider.value)
            : resolveFinpspDepositTypes(gatewayConfig, provider.value);

        if (types.length === 0) {
          return false;
        }
      }

      if (transactionType === PAYMENT_TRANSACTION_TYPE.WITHDRAW) {
        return !provider.disableWithdraw;
      }

      return true;
    });
  }, [paymentProviders, transactionType, paymentGatewayOptions, paymentGateway]);

  return filteredProviders;
};

export default usePaymentProviders;
